// Core functionality of redux state is tied to these methods and param reassignment.
// Since Redux is basically legacy code at this point we are grandfathering in these files. The rule will be enforced for any new code committed.
/* eslint-disable no-param-reassign */

export default class SearchParameterInteraction {
  constructor(galleryState) {
    this.searchParameters = galleryState.searchParameters;
    this.queryCorrections = galleryState.queryCorrections;
    this.searchProfile = galleryState.searchProfile;
  }

  updateSearchParametersAfterChangedParameterValue(
    searchParameters,
    changedParameterName
  ) {
    const newValue = searchParameters[changedParameterName];
    const isRemoval = newValue === undefined;

    switch (changedParameterName) {
      case '_colorindex': // mutually exclusive with bwonly parameter
        if (!isRemoval) delete searchParameters.bwonly;
        break;
      case 'bwonly': // mutually exclusive with _colorindex parameter
        // Legacy parameter name
        // eslint-disable-next-line no-underscore-dangle
        if (!isRemoval) delete searchParameters._colorindex;
        break;
      case 'recency':
        if (newValue !== 'daterange') {
          delete searchParameters.begindate;
          delete searchParameters.enddate;
        }
        break;
      // TODO: this should possibly live elsewhere since it's about a single parameter rather than an interaction between different params:
      case 'alloweduse':
        // we need to pass this 'off' value one time after clearing alloweduse so the associated cookie updates correctly (server-side)
        if (isRemoval) searchParameters.alloweduse = ['off'];
        break;
      case 'phraseprocessing':
        if (isRemoval) searchParameters.phraseprocessing = 'default';
        break;
      case 'phrase':
        delete searchParameters.stackid; // new phrase means we're probably no longer interested in this "same series" search
        delete searchParameters.clarifications;
        delete searchParameters.recentlyviewed;
        delete searchParameters.curatedset; // new phrase means we're not interested in the curated set
        break;
      case 'artistexact':
        delete searchParameters.morelikethis;
        break;
      case 'assettype':
        delete searchParameters.artistexact;
        delete searchParameters.recentlyviewed;
        delete searchParameters.compositions;
        break;

      case 'recentlyviewed':
        if (!isRemoval) {
          delete searchParameters.phrase;
        }
        break;
      case 'videodurationmin':
        if (isRemoval) {
          delete searchParameters.videodurationmax;
        }
        break;
      case 'offlinecontent':
        if (isRemoval) {
          searchParameters.offlinecontent = 'exclude';
        }
        break;
      case 'mediatype':
        if (isRemoval) {
          delete searchParameters.assetfiletype;
        }
        break;
      default:
        break;
    }
  }

  #handleSearchPhraseUpdates = (updatedSearchParameters) => {
    const newPhrase = updatedSearchParameters.phrase || '';
    const oldPhrase = this.searchParameters.phrase || '';

    const isPhraseAppendedTo =
      oldPhrase && newPhrase.toLowerCase().includes(oldPhrase.toLowerCase());

    if ((newPhrase || oldPhrase) && !isPhraseAppendedTo) {
      delete updatedSearchParameters.specificpeople;

      delete updatedSearchParameters.suppressfamilycorrection;
      delete updatedSearchParameters.suppressphrasecorrection;
      // only for editorial images, if phrase is changed and phraseprocessing is default (NLS on)
      // then we want to change phraseprocessing to excludenaturallanguage
      // this is to avoid the NLS processing on the new phrase, which is not desired
      if (
        updatedSearchParameters.family === 'editorial' &&
        updatedSearchParameters.assettype === 'image' &&
        updatedSearchParameters.phraseprocessing === 'includenaturallanguage'
      ) {
        updatedSearchParameters.phraseprocessing = 'excludenaturallanguage';
      }
    }
  };

  applySearchParameterInteractions(updatedSearchParameters) {
    let pageHasChanged = false;
    let nonPageParameterHasChanged = false;

    Object.keys(updatedSearchParameters).forEach((param) => {
      // JSON.stringify handles any array value comparisons correctly.
      const newValue = updatedSearchParameters[param];
      const oldValueJson = JSON.stringify(this.searchParameters[param]);
      const newValueJson = JSON.stringify(newValue);
      if (oldValueJson !== newValueJson) {
        if (param === 'page') {
          pageHasChanged = true;
        }
        if (param !== 'page') {
          nonPageParameterHasChanged = true;
        }
        this.updateSearchParametersAfterChangedParameterValue(
          updatedSearchParameters,
          param
        );
      }
    });

    Object.keys(this.searchParameters).forEach((param) => {
      const value = this.searchParameters[param];
      if (
        typeof value !== 'undefined' &&
        typeof updatedSearchParameters[param] === 'undefined'
      ) {
        // value was removed, so won't be found by the previous iteration:
        if (param === 'page') {
          pageHasChanged = true;
        }
        if (param !== 'page') {
          nonPageParameterHasChanged = true;
        }
        this.updateSearchParametersAfterChangedParameterValue(
          updatedSearchParameters,
          param
        );
      }
    });

    this.#handleSearchPhraseUpdates(updatedSearchParameters);

    // if any search parameter is updates while viewing recently viewed,
    // remove recently viewed context by deleeting the query param.
    delete updatedSearchParameters.recentlyviewed;

    // any meaningful parameter change should throw away any existing page number and start on page 1 (unless told otherwise).
    if (nonPageParameterHasChanged && !pageHasChanged) {
      updatedSearchParameters.page = 1;
    }
  }

  applyStateUpdates(stateUpdates) {
    this.applySearchParameterInteractions(stateUpdates.searchParameters);
  }
}
